import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Zap, ArrowLeft, Check, X as XIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SEOHead from "@/components/SEOHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const POLL_INTERVAL_MS = 1800;
const POLL_MAX_ATTEMPTS = 12; // ~22s

export default function BillingPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'monthly' | 'annual' | 'trial' | null
  const [pollState, setPollState] = useState(null); // { status, attempts }

  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");
  const requestedPkg = params.get("pkg");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/billing/status`);
      setBilling(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (canceled) {
      toast.info("Checkout cancelled — no charge made");
      params.delete("canceled");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-launch checkout if redirected from /pricing with ?pkg= and not currently polling.
  useEffect(() => {
    if (requestedPkg && !sessionId && !busy && billing && billing.plan !== "pro") {
      params.delete("pkg");
      setParams(params, { replace: true });
      handleCheckout(requestedPkg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedPkg, billing]);

  // Poll on return from Stripe Checkout.
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    setPollState({ status: "pending", attempts: 0 });

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      setPollState({ status: "pending", attempts });
      try {
        const { data } = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
        if (data.status === "paid") {
          toast.success("Payment confirmed — welcome to Pro!");
          await Promise.all([load(), refresh()]);
          setPollState({ status: "paid", attempts });
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          toast.error("Payment didn't complete — try again or use a different card.");
          setPollState({ status: data.status, attempts });
          return;
        }
        if (attempts >= POLL_MAX_ATTEMPTS) {
          setPollState({ status: "timeout", attempts });
          toast.error("Still waiting on Stripe — refresh in a moment.");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setPollState({ status: "error", attempts });
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleCheckout = async (pkg) => {
    setBusy(pkg);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        package: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(null);
    }
  };

  const handleTrial = async () => {
    setBusy("trial");
    try {
      await axios.post(`${API}/billing/trial/start`);
      toast.success("14-day Pro trial started!");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start trial");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead title="Billing — BreakItDown" path="/billing" noindex />
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/app")} className="text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5" data-testid="billing-back">
            <ArrowLeft className="w-4 h-4" /> <span className="text-sm">App</span>
          </button>
          <h1 className="text-base font-semibold tracking-tight">Billing</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 space-y-8">

        {pollState && pollState.status === "pending" && (
          <div className="border border-zinc-200 bg-zinc-50 p-4 flex items-center gap-3" data-testid="billing-poll-banner">
            <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
            <p className="text-sm text-zinc-700">Confirming your payment with Stripe… attempt {pollState.attempts}/{POLL_MAX_ATTEMPTS}.</p>
          </div>
        )}

        {/* Current plan */}
        <section className="border border-zinc-200 p-5 md:p-6" data-testid="billing-current-plan">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Current plan</p>
          <div className="flex items-end justify-between mt-2 flex-wrap gap-3">
            <div>
              <p className="text-3xl font-semibold tracking-tight text-zinc-900">
                {billing.plan === "pro" ? "Pro" : billing.plan === "trialing" ? "Pro · Trial" : "Free"}
                {billing.grandfathered && <span className="ml-2 text-xs uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 px-2 py-1 border border-emerald-200">Grandfathered</span>}
              </p>
              <p className="text-sm text-zinc-500 mt-1">
                {billing.grandfathered
                  ? "Lifetime Pro access — thank you for being an early user."
                  : billing.plan === "trialing"
                    ? `Trial expires in ${billing.days_remaining} day${billing.days_remaining === 1 ? "" : "s"}`
                    : billing.plan === "pro"
                      ? `Pro until ${formatDate(billing.expires_at)} (${billing.days_remaining} day${billing.days_remaining === 1 ? "" : "s"} remaining)`
                      : `${billing.list_count} of ${billing.list_limit} lists used`}
              </p>
            </div>
            {billing.plan === "free" && billing.can_start_trial && (
              <button
                onClick={handleTrial}
                disabled={!!busy}
                className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-xs font-medium uppercase tracking-[0.12em] transition-colors disabled:opacity-50"
                data-testid="billing-start-trial"
              >
                {busy === "trial" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Start 14-day trial
              </button>
            )}
          </div>
        </section>

        {/* Plan options — hide if grandfathered */}
        {!billing.grandfathered && (
          <section data-testid="billing-tiers">
            <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-4">
              {billing.plan === "free" ? "Upgrade to Pro" : "Extend or switch cadence"}
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <BillingTierCard
                pkg="monthly"
                name="Pro · Monthly"
                price="$15"
                cadence="AUD / month"
                bullet="30 days of Pro access. Cancel anytime."
                badge="Most popular"
                busy={busy}
                onClick={() => handleCheckout("monthly")}
              />
              <BillingTierCard
                pkg="annual"
                name="Pro · Annual"
                price="$120"
                cadence="AUD / year"
                bullet="365 days of Pro. Saves $60 vs monthly."
                badge="Best value · save 33%"
                accent
                busy={busy}
                onClick={() => handleCheckout("annual")}
              />
            </div>
          </section>
        )}

        {/* Pro features summary */}
        <section className="border border-zinc-200 p-5 md:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3">What Pro unlocks</p>
          <ul className="text-sm space-y-2 text-zinc-700">
            {[
              "Unlimited custom task lists",
              "Share a list with another user (by email)",
              "Share your whole workspace",
              "3 permission levels: View / Edit / Manage",
              `"Shared with me" section in the sidebar`,
              "Priority support",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> {f}</li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

function BillingTierCard({ pkg, name, price, cadence, bullet, badge, accent, busy, onClick }) {
  return (
    <div className={`border p-5 ${accent ? "border-zinc-900" : "border-zinc-200"}`} data-testid={`billing-tier-${pkg}`}>
      {badge && (
        <div className={`inline-block text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-1 mb-3 ${accent ? "bg-[#002FA7] text-white" : "border border-zinc-900 text-zinc-900"}`}>
          {badge}
        </div>
      )}
      <h3 className="text-base font-semibold tracking-tight text-zinc-900">{name}</h3>
      <div className="mt-2 flex items-end gap-2">
        <span className="text-3xl font-semibold tracking-tight text-zinc-900">{price}</span>
        <span className="text-xs text-zinc-500 pb-1">{cadence}</span>
      </div>
      <p className="text-xs text-zinc-500 mt-2">{bullet}</p>
      <button
        onClick={onClick}
        disabled={!!busy}
        className={`mt-5 w-full text-xs font-medium uppercase tracking-[0.12em] px-4 py-2.5 transition-colors disabled:opacity-50 ${
          accent ? "bg-[#002FA7] hover:bg-zinc-900 text-white" : "bg-zinc-900 hover:bg-[#002FA7] text-white"
        }`}
        data-testid={`billing-checkout-${pkg}`}
      >
        {busy === pkg ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</span>
        ) : (
          `Pay ${price} →`
        )}
      </button>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
  catch { return iso; }
}
